import type {
    DefaultObject,
    PaginatedResponse,
    Value,
} from '@moniebook-web/types';
import type { useTableState } from '@moniebook-web/components/molecules/table';

import { Axios } from './api';

type FilterValue = Value | null;

export const PAGINATION_BATCH_SIZE = 100;
export const MAX_FETCH_PROGRESS = 1;

interface PaginationOptions {
    maxItems?: number;
    startPage?: number;
}

export async function retrieveFullPaginatedData<Data>(
    url: string,
    params: Record<string, unknown>,
    onCallback: (
        data: Data[],
        progress: number,
        nextPage?: number,
    ) => Promise<void>,
    batchSize = PAGINATION_BATCH_SIZE,
    paginationOptions?: PaginationOptions,
): Promise<{ hasMore: boolean; nextPage: number }> {
    let totalPages = 0;
    let page = paginationOptions?.startPage ?? 0;
    let totalFetched = 0;
    let hasMoreData = true;

    while (hasMoreData) {
        const response = await Axios.get<PaginatedResponse<Data>>(url, {
            params: { ...params, page, size: batchSize },
        });

        let progress = 0;
        totalPages = Math.max(totalPages, response.data.totalPages);

        // First page is mostly inaccurate
        if (page > 0) {
            progress = Math.min((page + 1 * 1.0) / totalPages, 1);
        }

        if (response?.data.content.length) {
            await onCallback(response.data.content, progress, page + 1);
            totalFetched += response.data.content.length;
            page++;

            if (
                paginationOptions?.maxItems !== undefined &&
                totalFetched >= paginationOptions.maxItems
            ) {
                return { hasMore: totalPages > page, nextPage: page };
            }
        } else {
            hasMoreData = false;
        }
    }

    return { hasMore: false, nextPage: page };
}

export function getTableQueryParams<
    FilterParams extends Record<keyof FilterParams, FilterValue>,
>(
    filter: Partial<FilterParams>,
    tableState: ReturnType<typeof useTableState>,
): DefaultObject {
    const { pagination } = tableState;
    const { pageIndex, pageSize } = pagination;

    return {
        ...(filter as DefaultObject),
        page: `${pageIndex ?? 0}`,
        size: `${pageSize ?? 10}`,
    };
}
